import React from 'react';
import {hydrate, render} from 'react-dom';
import {Provider} from 'react-redux';
import {loadableReady} from '@loadable/component';
import AbacusClientUtils from '@homeaway/abacus-client-utils';
import {
    CookieProvider,
    vrboCookieConsentAdapter,
} from '@homeaway/react-cookie-context';
import {configureStore} from '../../shared/startup/configureStore';
import {withErrorBoundary} from '../../shared/components/common/error-boundary/ErrorBoundary';
import LazyLoad from '../../shared/components/common/lazy-load/LazyLoad';
import {LAZY_LOAD_OFFSET} from '../../shared/utils/constants';
import {SearchFormContainer} from '../../shared/components/common/search-form/SearchForm';
import {StickySearchFormWrapper} from '../../shared/components/common/search-form/StickySearchForm/StickySearchForm';
import {TESTS} from '../../shared/utils/abacus';
import mkApolloClient from './client';
import {ApolloProvider} from 'react-apollo';
import {LocalizationContextProvider} from '@homeaway/react-localization-components';
import {exposeOnGdprConsent} from '../../shared/utils/edap';
import {UserActivityProvider} from '../../shared/components/common/user-activity';

const {DEFAULT: LAZY_LOAD_OFFSET_DEFAULT} = LAZY_LOAD_OFFSET;

if (
    process.env.NODE_ENV === 'development' &&
    process.env.LANDINGAPP_DEBUG_REACT === '1'
) {
    const whyDidYouRender = require('@welldone-software/why-did-you-render');
    whyDidYouRender(React, {
        trackAllPureComponents: true,
        logOwnerReasons: true,
        collapseGroups: true,
    });
}

function hydrateContent(store, siteContext, apolloClient, Template) {
    return loadableReady().then(() => {
        hydrate(
            <ApolloProvider client={apolloClient}>
                <Provider store={store}>
                    <LocalizationContextProvider siteContext={siteContext}>
                        <UserActivityProvider>
                            <Template />
                        </UserActivityProvider>
                    </LocalizationContextProvider>
                </Provider>
            </ApolloProvider>,
            document.querySelector('.content-root')
        );
    });
}

/* eslint consistent-return:0 */
export default function hydrateApp(Template) {
    // Grab the state from a global injected into server-generated HTML
    const preloadedState = window.__PRELOADED_STATE__;

    // Create Redux store with initial state
    const store = configureStore({
        initialState: preloadedState,
    });

    const {
        abacus,
        config: {
            url: {prefix},
            context: {userLocation},
        },
        options: {allowStickySearchForm},
        siteContext,
    } = store.getState();

    const apolloClient = mkApolloClient({prefix, siteContext, userLocation});

    window.__SITE_CONTEXT__ = siteContext;

    AbacusClientUtils.initialize(abacus);

    if (abacus[TESTS.POLYFILL_SERVICE]) {
        AbacusClientUtils.logExperiment(abacus[TESTS.POLYFILL_SERVICE]);
    }

    exposeOnGdprConsent(TESTS.VRBO_LANDING_DATADOG_FOR_RUM);

    const SearchComponent = allowStickySearchForm
        ? StickySearchFormWrapper
        : SearchFormContainer;

    hydrate(
        <ApolloProvider client={apolloClient}>
            <Provider store={store}>
                {withErrorBoundary(<SearchComponent />)}
            </Provider>
        </ApolloProvider>,
        document.querySelector('.searchbox-root')
    );

    import(
        /* webpackChunkName: "site-header" */
        /* webpackPreload: true */
        '../../shared/components/common/site-header/SiteHeader'
    ).then((module) => {
        const SiteHeader = module.default;
        render(
            <CookieProvider
                cookies={document.cookie}
                adapter={vrboCookieConsentAdapter}
            >
                <ApolloProvider client={apolloClient}>
                    <Provider store={store}>
                        <LocalizationContextProvider siteContext={siteContext}>
                            {withErrorBoundary(<SiteHeader />)}
                        </LocalizationContextProvider>
                    </Provider>
                </ApolloProvider>
            </CookieProvider>,
            document.querySelector('.site-header-root')
        );
    });

    import(
        /* webpackChunkName: "hero-image" */
        /* webpackPreload: true */
        '../../shared/modules/HeroImage'
    ).then((module) => {
        const HeroImage = module.default;
        hydrate(
            <ApolloProvider client={apolloClient}>
                <Provider store={store}>
                    <LocalizationContextProvider siteContext={siteContext}>
                        {withErrorBoundary(<HeroImage />)}
                    </LocalizationContextProvider>
                </Provider>
            </ApolloProvider>,
            document.querySelector('.hero-image-root')
        );
    });

    hydrateContent(store, siteContext, apolloClient, Template);

    import(
        /* webpackChunkName: "site-footer" */
        '../../shared/components/common/site-footer/AsyncSiteFooter'
    ).then(({AsyncSiteFooter}) => {
        render(
            <ApolloProvider client={apolloClient}>
                <Provider store={store}>
                    <LazyLoad offset={LAZY_LOAD_OFFSET_DEFAULT}>
                        {withErrorBoundary(<AsyncSiteFooter />)}
                    </LazyLoad>
                </Provider>
            </ApolloProvider>,
            document.querySelector('.site-footer-root')
        );
    });
}
